/**
 * Teams, from the gateway: who works together and who leads them.
 *
 * Teams live beside the users they group, because what a team changes is
 * what a login may see: a sales rep who leads a team sees the team's
 * people, companies and deals. The org server reads them from the session,
 * so a change reaches everyone's lists within about five minutes, the same
 * as a change of role.
 *
 * Every shape comes from `gateway-schema.d.ts`, generated from the gateway's
 * own structs.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { components } from './gateway-schema';
import { createTeam, deleteTeam, listTeams, renameTeam, setTeamMembers } from './gateway';

type S = components['schemas'];
export type Team = S['Team'];
export type TeamMember = S['TeamMember'];
export type TeamMemberInput = S['TeamMemberInput'];

const KEY = (tenantId: string | undefined) => ['teams', tenantId];

export function useTeams(tenantId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: KEY(tenantId),
    queryFn: () => listTeams(tenantId!),
    enabled: enabled && !!tenantId,
    staleTime: 30 * 1000,
  });
}

export function useTeamMutations(tenantId: string | undefined) {
  const qc = useQueryClient();
  const done = () => qc.invalidateQueries({ queryKey: KEY(tenantId) });
  return {
    create: useMutation({ mutationFn: (name: string) => createTeam(tenantId!, name), onSuccess: done }),
    rename: useMutation({
      mutationFn: ({ id, name }: { id: string; name: string }) => renameTeam(id, name),
      onSuccess: done,
    }),
    remove: useMutation({ mutationFn: (id: string) => deleteTeam(id), onSuccess: done }),
    members: useMutation({
      mutationFn: ({ id, members }: { id: string; members: TeamMemberInput[] }) => setTeamMembers(id, members),
      onSuccess: done,
    }),
  };
}

/** A member's name as the teams screen shows it. */
export function memberName(m: Pick<TeamMember, 'first_name' | 'last_name' | 'email'>): string {
  const n = [m.first_name, m.last_name].filter((x) => x && x.trim()).join(' ');
  return n || m.email;
}
